// Join.js - TRAKtag Group Quiz
// Version: 2.0
// Features: 6-char code, quiz name preview, countdown, client logo, ad space

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const AVATARS = ['😀', '😎', '🤓', '🥳', '🤠', '👻', '🦊', '🐱', '🦁', '🐸', '🦄', '🐲'];

function Join() {
  const { code: urlCode } = useParams();
  const navigate = useNavigate();
  
  const [joinCode, setJoinCode] = useState(urlCode || '');
  const [playerName, setPlayerName] = useState('');
  const [avatar, setAvatar] = useState('😀');
  const [step, setStep] = useState(urlCode ? 2 : 1);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    if (urlCode) {
      checkSession(urlCode);
    }
  }, [urlCode]);

  // Countdown timer
  useEffect(() => {
    if (!session?.scheduledStartAt) {
      setCountdown('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const start = new Date(session.scheduledStartAt);
      const diff = start - now;

      if (diff <= 0) {
        setCountdown('Starting soon...');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setCountdown(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${minutes}m ${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [session]);

  const checkSession = async (code) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.joinSession(code, 'temp', '😀');
      setSession(response.session);
      setStep(2);
    } catch (err) {
      setError('Please check your code. The code entered is not a listed Quiz.');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (joinCode.length === 6) {
      checkSession(joinCode);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.joinSession(joinCode, playerName, avatar);
      localStorage.setItem('sessionToken', response.sessionToken);
      localStorage.setItem('participantId', response.participant.id);
      localStorage.setItem('playerName', response.participant.playerName);
      localStorage.setItem('playerAvatar', response.participant.avatarEmoji);
      navigate(`/play/${response.session.id}`);
    } catch (err) {
      setError(err.message || 'Failed to join');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="join-page">
      {/* Client/TRAKtag Logo */}
      <div className="join-logo">
        {session?.clientLogoUrl ? (
          <img src={session.clientLogoUrl} alt="Quiz Logo" className="client-logo" />
        ) : (
          <div className="traktag-logo">🎯 TRAKtag</div>
        )}
      </div>

      <div className="join-card">
        <h1>Join Quiz</h1>
        
        {step === 1 && (
          <>
            <p className="subtitle">Enter the quiz code from your host</p>
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleCodeSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="ABC123"
                  className="code-input"
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>
              
              <button type="submit" className="btn btn-primary" disabled={loading || joinCode.length !== 6}>
                {loading ? 'Checking...' : '🎮 Find Quiz'}
              </button>
            </form>

            {/* App Promo */}
            <div className="app-promo">
              <h3>📱 Better in the App!</h3>
              <p>Get push notifications, haptic feedback, and smoother gameplay</p>
              <a href="/download" className="app-link">Download TRAKtag App →</a>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {/* Quiz Info Preview */}
            <div className="quiz-preview">
              <div className="quiz-preview-icon">🎪</div>
              <h2 className="quiz-preview-title">{session?.title || 'Quiz'}</h2>
              {session?.hostName && (
                <p className="quiz-preview-host">Host: {session.hostName}</p>
              )}
              {countdown && (
                <div className="quiz-preview-countdown">
                  <span className="countdown-label">Starts in:</span>
                  <span className="countdown-time">{countdown}</span>
                </div>
              )}
              {!session?.scheduledStartAt && (
                <p className="quiz-preview-waiting">Waiting for host to start</p>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleJoin}>
              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Enter your name"
                  maxLength={30}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Choose Avatar</label>
                <div className="avatar-picker">
                  {AVATARS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className={`avatar-option ${avatar === emoji ? 'selected' : ''}`}
                      onClick={() => setAvatar(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Joining...' : '🎮 Join Quiz Now!'}
              </button>
              
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setStep(1); setError(''); setSession(null); }}
              >
                ← Back
              </button>
            </form>
          </>
        )}

        {/* Ad Space */}
        <div className="ad-space">
          <div className="ad-placeholder">
            📺 Ad Space - 320x50
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="join-footer">
        Powered by <strong>TRAKtag</strong>
      </div>
    </div>
  );
}

export default Join;
